/**
 * InstantBookingModal — Customer-side, opens when user taps the
 * "Book Instantly ⚡" CTA on a search card.
 *
 * Flow:
 *   1. Eligibility pre-check (frontend: supplier.is_instant_bookable)
 *   2. User picks duration, adds optional note
 *   3. POST /api/bookings/instant
 *   4a. If auto-accepted → "Booking confirmed instantly 🎉"
 *   4b. Otherwise → "Booking request sent 🎉"
 *
 * Copy follows the spec — speed + confidence + urgency.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lightning, X, CalendarCheck, MapPin, Clock, Eye } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DURATIONS = [2, 3, 4, 5, 6, 8];

const InstantBookingModal = ({ supplier, date, location, eventId, onClose, onOpenChat }) => {
  const navigate = useNavigate();
  const [duration, setDuration] = useState(4);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {chat_id, auto_confirmed}
  const [errorState, setErrorState] = useState(null); // {type, message}
  const [socialProof, setSocialProof] = useState(false);

  // Fire a profile-view ping when the modal mounts — this both
  // registers the current viewer AND returns whether the supplier
  // has hit the ≥3-unique-viewer threshold in the last 24h.
  useEffect(() => {
    if (!supplier?.id) return;
    let alive = true;
    axios
      .post(`${API}/suppliers/${supplier.id}/view`)
      .then((r) => { if (alive) setSocialProof(!!r.data?.show); })
      .catch(() => {});
    return () => { alive = false; };
  }, [supplier?.id]);

  const firstName = (supplier?.business_name || 'this supplier').split(' ')[0];
  const humanDate = formatHumanDate(date);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setErrorState(null);
    try {
      const { data } = await axios.post(`${API}/bookings/instant`, {
        supplier_id: supplier.id,
        date,
        duration_hours: duration,
        event_id: eventId || null,
        location: location || '',
        note: note.trim(),
      });
      setResult({ chat_id: data.chat_id, auto_confirmed: data.auto_confirmed });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      if (status === 401) {
        toast.error('Please log in to book');
        navigate('/login');
        onClose?.();
        return;
      }
      if (status === 409 && detail?.error === 'supplier_at_capacity') {
        setErrorState({ type: 'capacity', message: detail.message });
      } else if (status === 409 && detail?.error === 'unavailable_on_date') {
        setErrorState({ type: 'unavailable', message: detail.message });
      } else if (status === 400 && detail?.error === 'not_instant_eligible') {
        setErrorState({ type: 'ineligible', message: detail.message });
      } else {
        toast.error(detail?.message || detail || 'Could not send booking');
      }
    } finally {
      setBusy(false);
    }
  };

  const close = (e) => {
    if (e) e.stopPropagation();
    onClose?.();
  };

  // ── SUCCESS STATE ──
  if (result) {
    const confirmed = result.auto_confirmed;
    return (
      <Shell onClose={close}>
        <div className="text-center" data-testid="instant-booking-success">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          >
            <CalendarCheck size={28} weight="fill" className="text-white" />
          </div>
          <h2 className="text-2xl font-black mb-1 text-white" data-testid="instant-booking-success-title">
            {confirmed ? 'Booking confirmed instantly 🎉' : 'Booking request sent 🎉'}
          </h2>
          <p className="text-white/60 text-sm mb-3">
            {confirmed
              ? `${supplier.business_name} is available and your booking is secured. No waiting — you're all set.`
              : `${supplier.business_name} has received your request. They usually reply within minutes.`}
          </p>
          {socialProof && (
            <p
              className="text-[11px] font-bold text-[#C29D52]/85 mb-4 italic"
              data-testid="instant-booking-secured-before"
            >
              You secured this before someone else did 🎉
            </p>
          )}
          <div className="space-y-2">
            <button
              onClick={() => { onOpenChat?.(result.chat_id); close(); }}
              className="w-full py-3 rounded-xl text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="instant-booking-view"
            >
              View booking
            </button>
            <button
              onClick={close}
              className="w-full py-2.5 rounded-xl text-xs font-bold text-white/50 hover:text-white/80"
              data-testid="instant-booking-close"
            >
              Close
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // ── ERROR STATES ──
  if (errorState?.type === 'capacity') {
    return (
      <Shell onClose={close}>
        <div className="text-center" data-testid="instant-booking-capacity">
          <Lightning size={28} weight="fill" className="mx-auto mb-3 text-amber-300" />
          <h2 className="text-xl font-black mb-2 text-white">Fully booked right now</h2>
          <p className="text-white/60 text-sm mb-5">
            This supplier has reached their instant booking limit. You can still message them directly to check availability.
          </p>
          <button
            onClick={close}
            className="w-full py-3 rounded-xl text-sm font-bold text-white bg-white/10 border border-white/15"
            data-testid="instant-booking-capacity-close"
          >
            Close
          </button>
        </div>
      </Shell>
    );
  }

  if (errorState?.type === 'unavailable' || errorState?.type === 'ineligible') {
    return (
      <Shell onClose={close}>
        <div className="text-center" data-testid="instant-booking-error">
          <X size={28} weight="bold" className="mx-auto mb-3 text-white/50" />
          <h2 className="text-xl font-black mb-2 text-white">
            {errorState.type === 'unavailable' ? 'No longer available' : 'Not quite ready'}
          </h2>
          <p className="text-white/60 text-sm mb-5">{errorState.message}</p>
          <button
            onClick={close}
            className="w-full py-3 rounded-xl text-sm font-bold text-white bg-white/10 border border-white/15"
          >
            Close
          </button>
        </div>
      </Shell>
    );
  }

  // ── MAIN MODAL ──
  return (
    <Shell onClose={close}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-xl bg-cover bg-center shrink-0 flex items-center justify-center"
          style={{
            background: supplier.images?.[0]
              ? `url(${supplier.images[0]}) center/cover`
              : 'linear-gradient(135deg, rgba(108,85,224,0.3), rgba(194,157,82,0.2))',
          }}
        >
          {!supplier.images?.[0] && <Lightning size={18} weight="fill" className="text-white/60" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black tracking-widest uppercase text-[#C29D52] flex items-center gap-1">
            <Lightning size={10} weight="fill" /> Instant Booking
          </p>
          <h2 className="text-lg font-black leading-tight text-white" data-testid="instant-booking-supplier-name">
            Book {supplier.business_name} instantly
          </h2>
        </div>
      </div>

      {/* Date + Location */}
      <div className="rounded-xl p-3 mb-4 bg-white/[0.03] border border-white/10 space-y-1.5">
        <div className="flex items-center gap-2 text-sm text-white">
          <CalendarCheck size={14} weight="fill" className="text-[#C29D52]" />
          <span className="font-bold">{humanDate}</span>
        </div>
        {location && (
          <div className="flex items-center gap-2 text-xs text-white/60">
            <MapPin size={12} weight="bold" />
            <span>{location}</span>
          </div>
        )}
        <p className="text-[11px] text-emerald-300 font-bold flex items-center gap-1 pt-1">
          ✓ This date is available — secure it now
        </p>
      </div>

      {/* Duration */}
      <div className="mb-4">
        <label className="block text-[10px] font-black tracking-wider uppercase text-white/40 mb-2">
          Duration
        </label>
        <div className="flex gap-1.5 flex-wrap" data-testid="duration-picker">
          {DURATIONS.map((h) => (
            <button
              key={h}
              onClick={() => setDuration(h)}
              className="px-3 py-2 rounded-lg text-xs font-bold border transition-all"
              style={{
                background: duration === h ? 'linear-gradient(135deg, #B89762, #C29D52)' : 'rgba(255,255,255,0.04)',
                borderColor: duration === h ? 'transparent' : 'rgba(255,255,255,0.1)',
                color: duration === h ? 'white' : 'rgba(255,255,255,0.7)',
              }}
              data-testid={`duration-${h}`}
            >
              <Clock size={11} weight="bold" className="inline -mt-0.5 mr-1" />
              {h}h
            </button>
          ))}
        </div>
      </div>

      {/* Optional note */}
      <div className="mb-5">
        <label className="block text-[10px] font-black tracking-wider uppercase text-white/40 mb-2">
          Notes (optional)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 500))}
          placeholder="Anything the supplier should know?"
          rows={2}
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B89762]/40/40 resize-none"
          data-testid="instant-booking-note"
        />
      </div>

      {/* Reassurance */}
      <p className="text-white/50 text-xs text-center mb-4 leading-relaxed">
        You're sending a booking request directly to <strong className="text-white/70">{supplier.business_name}</strong>.<br />
        They usually reply within minutes.
      </p>

      {socialProof && (
        <div
          className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-[#C29D52]/85 mb-3"
          data-testid="social-proof-line"
        >
          <Eye size={11} weight="fill" />
          Other customers are currently viewing this supplier
        </div>
      )}

      {/* Confirm */}
      <button
        onClick={confirm}
        disabled={busy}
        className="w-full py-3.5 rounded-xl text-sm font-black text-white flex items-center justify-center gap-2 disabled:opacity-60"
        style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        data-testid="confirm-instant-booking-btn"
      >
        <Lightning size={14} weight="fill" />
        {busy ? 'Sending…' : 'Confirm booking'}
      </button>
    </Shell>
  );
};

const Shell = ({ children, onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
    onClick={onClose}
    data-testid="instant-booking-modal"
  >
    <div
      className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-5 bg-[#15161B] border border-white/10 max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-end mb-2">
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
          data-testid="instant-booking-close-x"
        >
          <X size={12} weight="bold" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

function formatHumanDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default InstantBookingModal;
